import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'

import { DateTime } from 'luxon'
import { getResource } from '@/shared/constants/resources'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { checkBlockCondition as checkBlockConditionPure } from '@/shared/lib/blocker/checkBlockCondition'
import type { GetProgress } from '@/shared/lib/blocker/checkBlockCondition'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { showBlockAlertDialog } from '@/shared/ui/dialog'
import type {
  BlockCondition,
  Blocker,
  BlockResult,
  TimeBlocker
} from '@/types/blocker'
import type { Resource } from '@/types/resource'

/**
 * Decide lock/unlock a partir do que o cliente já tem: trilhas e missões do
 * bundle mais o progresso do participante. É o precedente do critério de
 * derivação — ver `shared/lib/derivation/CRITERIO.md`.
 *
 * Errar aqui mostra ou esconde um cadeado; não concede progresso. A conclusão
 * continua validada em `progress#finish_resource`, a partir do progresso
 * autenticado.
 */

export const useBlocker = () => {
  const { navigateTo } = useNavigation()

  const {
    journeyWasFinished,
    missionWasFinished,
    levelWasFinished,
    partnerWasFinished
  } = useUserProgress()

  const { journeys: journeysRaw, missions: missionsRaw } =
    useGamificationDataStore(s => s.getActiveProductData())

  const generateConditions = (resource: Resource) => {
    let conditions: BlockCondition[] = []

    if ('unlock_journey_id' in resource && resource?.unlock_journey_id) {
      const journey =
        resource?.unlock_journey ||
        journeysRaw.find(j => j.id === resource?.unlock_journey_id)

      conditions.push({
        type: 'resource',
        resource: {
          type: 'journey',
          id: resource.unlock_journey_id,
          uid: journey.uid,
          name: journey.name
        },
        condition: 'finished'
      })
    }

    if ('unlock_mission_id' in resource && resource?.unlock_mission_id) {
      const mission =
        resource?.unlock_mission ||
        missionsRaw.find(j => j.id === resource?.unlock_mission_id)

      conditions.push({
        type: 'resource',
        resource: {
          type: 'mission',
          id: resource.unlock_mission_id,
          uid: mission.uid,
          name: mission.name
        },
        condition: 'finished'
      })
    }

    if ('locked_until' in resource && !!resource?.locked_until) {
      conditions.push({
        type: 'time',
        until: resource?.locked_until
      })
    }

    return conditions
  }

  const defaultGetProgress: GetProgress = (resource, condition) => {
    if (resource.type === 'journey') {
      return journeyWasFinished(resource.id)
    }

    if (resource.type === 'mission') {
      return missionWasFinished(resource.id)
    }

    if (resource.type === 'level') {
      if (condition === 'blockRedo') {
        return !levelWasFinished(resource.id)
      }

      return levelWasFinished(resource.id)
    }

    if (resource.type === 'partner') {
      return partnerWasFinished(resource.id)
    }

    return true
  }

  const checkBlockCondition = (
    block: BlockCondition,
    getProgress: GetProgress = defaultGetProgress
  ): BlockResult => checkBlockConditionPure(block, getProgress)

  const getBlockMessage = (blocker: Blocker | TimeBlocker) => {
    if (blocker.type === 'time')
      return {
        main: blocker.message
          ? blocker.message.replace(
              '{until}',
              DateTime.fromISO(blocker.until).toLocaleString(
                DateTime.DATETIME_MED
              )
            )
          : `Este recurso estará disponível em ${DateTime.fromISO(
              blocker.until
            ).toLocaleString(DateTime.DATETIME_MED)}`,
        confirmLabel: 'Ver depois',
        path: null,
        resource: null
      }

    const resource = getResource(blocker.resource.type)
    const name = blocker.resource.name || resource.humanName.singular

    // `resource: null` é parte da correção, não detalhe: com resource
    // preenchido o BlockAlertDialog acrescenta "Você pode acessar a/o nível e
    // concluir as etapas necessárias", instrução sem destino já que path é nulo.
    if (blocker.condition === 'custom') {
      return {
        main: blocker.message ?? 'Conclua os níveis exigidos para liberar',
        confirmLabel: 'Ok!',
        path: null,
        resource: null
      }
    }

    if (blocker.condition === 'blockRedo') {
      return {
        main: 'Você já completou este nível e não pode refazê-lo',
        confirmLabel: 'Ok!',
        path: null,
        resource
      }
    }

    const main = blocker.message
      ? blocker.message.replace('{name}', name)
      : `Para acessar este recurso, é necessário concluir a/o ${resource.humanName.singular.toLowerCase()} ${name}.`

    const confirmLabel = `Ver ${resource.humanName.singular.toLowerCase()} agora`

    let path = null
    if ('getResourcePath' in resource || 'basePath' in resource) {
      if ('getResourcePath' in resource) {
        path = resource.getResourcePath({
          resource: blocker.resource
        })
      } else {
        path = resource.basePath

        if (blocker.resource.uid) {
          path = `${path}/${blocker.resource.uid}`
        } else if (blocker.resource.id) {
          path = `${path}/${blocker.resource.id}`
        }
      }
    }

    return {
      main,
      confirmLabel,
      path,
      resource
    }
  }

  const showBlockAlert = async (blocker: Blocker | TimeBlocker) => {
    const { main, confirmLabel, path, resource } = getBlockMessage(blocker)
    const blockRedo =
      blocker.type === 'resource' && blocker.condition === 'blockRedo'
    const finishedKind =
      blocker.type === 'resource' && blocker.condition === 'finished'

    await showBlockAlertDialog({
      main,
      confirmLabel,
      path,
      resource,
      blockRedo,
      finishedKind,
      onNavigate: navigateTo
    })
  }

  return {
    generateConditions,
    checkBlockCondition,
    getBlockMessage,
    showBlockAlert
  }
}
